import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/utils/format";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { trackAddToCart } from "@/utils/gtm";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import type { ProductVariation } from "@/types/database";

interface ProductCardProps {
  id: string;
  name: string;
  slug: string;
  price: number;
  image: string;
  category: string;
  brand?: string;
  variations?: ProductVariation[];
}

const ProductCard = ({ id, name, slug, price, image, category, brand, variations }: ProductCardProps) => {
  const { addItem, setIsOpen } = useCart();

  // Calculate price display for products with multiple variations
  const hasMultipleVariations = variations && variations.length > 1;
  const minPrice = hasMultipleVariations 
    ? Math.min(...variations.map(v => v.price))
    : price;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    addItem({
      id: id,
      productId: id,
      productSlug: slug,
      name,
      price: minPrice,
      imageUrl: image,
    });

    // Track add_to_cart event
    trackAddToCart({
      id,
      name,
      price: minPrice,
      brand,
      category,
      quantity: 1,
    });
    
    toast.success("Producto agregado al carrito");
    setIsOpen(true);
  };

  return (
    <Card className="group overflow-hidden border-0 shadow-card hover:shadow-lg transition-all duration-300 bg-card flex flex-col">
      <Link to={`/producto/${slug}`}>
        <OptimizedImage
          src={image}
          alt={`${name} - ${brand || category} - Gordon Store`}
          containerClassName="aspect-square bg-white border border-border/50 rounded-t-lg"
          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
          useWebP={true}
        />
      </Link>
      <div className="p-4 space-y-2 flex-1 flex flex-col">
        {brand && (
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{brand}</p>
        )}
        <Link to={`/producto/${slug}`} className="flex-1">
          <h3 className="font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
            {name}
          </h3>
        </Link>
        <p className="text-2xl font-bold text-primary">
          {hasMultipleVariations && <span className="text-sm font-normal text-muted-foreground mr-1">Desde</span>}
          {formatPrice(minPrice)}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-2"
          onClick={handleAddToCart}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Agregar al carrito
        </Button>
      </div>
    </Card>
  );
};

export default ProductCard;
